import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

/**
 * This context provides a generic interface for implementing apps to inject
 * domain-specific logic into the core framework.
 */
export const AppSpecificContext = createContext({
  /**
   * A function to filter the main resource list before display in generic components
   * like the LibraryBrowser. The default implementation does nothing.
   * @param {Array} resources - The original array of resources.
   * @returns {Array} The filtered array of resources.
   */
  filterResourcesForDisplay: (resources) => resources,
  /**
   * A function that returns a resource organization function for list views.
   * The returned function is responsible for grouping resources.
   * @param {string|Array<string>} typeFilter - The type(s) of resources being displayed.
   * @returns {Function|null} A function to organize resources, or null for default behavior.
   */
  getResourceListOrganizer: (typeFilter) => null,
  /**
   * A function to determine if a resource can be dropped into another resource.
   * This allows apps to define their own hierarchy and container rules.
   * @param {object} childResource - The resource being dragged.
   * @param {object} parentResource - The potential drop target resource.
   * @returns {boolean} True if the drop is valid, false otherwise.
   */
  canResourceBeChildOf: (childResource, parentResource) => {
    const parentType = parentResource?.type || parentResource?.collection_type;
    // Default core logic: anything can be dropped into a collection/unit.
    if (parentType === 'collection' || parentType === 'unit') {
      return true;
    }
    // Deny all other drops by default.
    return false;
  },
});

export const useAppSpecifics = () => useContext(AppSpecificContext);

export const AppSpecificsProvider = ({ children, value }) => {
  return (
    <AppSpecificContext.Provider value={value}>
      {children}
    </AppSpecificContext.Provider>
  );
};

AppSpecificsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.shape({
    filterResourcesForDisplay: PropTypes.func,
    getResourceListOrganizer: PropTypes.func,
    canResourceBeChildOf: PropTypes.func,
  }).isRequired,
};